import { match } from '@st/pattern'
import type { ChecklistMessage } from '../module'
import {
  ChecklistItemRow,
  checklistDragProps,
  ChecklistVisibilityButton,
  ChecklistItemActions,
  ChecklistListActions,
  ChecklistEmptyState
} from '../list'
import type { checklistRows } from '../selectors'
import type { STChecklistItem, STDocument, STMissingReason } from '@st/sdk'
import type { DragHandlersProps, DragState } from '@st/react-util/use-draggable'
import type { PaginatedPage } from '@st/util/array'
import { Button, Checklist } from '@st/theme'
export function IntakeChecklist({
  send,
  rows,
  entityName,
  page,
  isArchived,
  year,
  type,
  sourceId,
  onSelectDocument,
  onRequestItems,
  dragHandlers,
  dragState
}: {
  entityName: string
  type: string
  year: number
  sourceId: string | undefined
  page: PaginatedPage<STChecklistItem>
  isArchived: boolean
  send: (message: ChecklistMessage) => void
  rows: ReturnType<typeof checklistRows>
  onSelectDocument: (documentId: string) => void
  onRequestItems: () => void
  dragHandlers: (item: STChecklistItem) => DragHandlersProps
  dragState: DragState<STChecklistItem>
}) {
  return (
    <div className="flex flex-col items-stretch">
      <div className="mb-2 flex items-start justify-between gap-6">
        {page.isFirst && <h1 className="min-w-0 flex-1 text-3xl">{`${entityName}'s Checklist`}</h1>}
        <div className="ml-auto flex shrink-0 flex-col items-end gap-2">
          {page.isFirst && (
            <dl className="grid grid-cols-[auto_auto] gap-x-2 text-xs">
              <dt className="text-right uppercase text-gray-600">Return type</dt>
              <dd className="text-gray-900">{type}</dd>
              <dt className="text-right uppercase text-gray-600">Tax year</dt>
              <dd className="text-gray-900">{year}</dd>
              {sourceId && (
                <>
                  <dt className="text-right uppercase text-gray-600">Client ID</dt>
                  <dd className="text-gray-900">{sourceId}</dd>
                </>
              )}
            </dl>
          )}
          <ChecklistListActions send={send} phase="intake" disabled={isArchived}>
            <Button variant="default" disabled={isArchived} onClick={onRequestItems}>
              Request documents
            </Button>
          </ChecklistListActions>
        </div>
      </div>
      {match(page.items.length == 0)
        .with(true, () => <ChecklistEmptyState instructions="Use Add item to get started." />)
        .with(false, () => (
          <div>
            <Checklist.Table>
              {rows.map(({ item, documents, incompleteReason }) => {
                return (
                  <STFolderChecklistItem
                    send={send}
                    documents={documents}
                    isArchived={isArchived}
                    key={item.id}
                    item={item}
                    incompleteReason={incompleteReason}
                    onSelectDocument={onSelectDocument}
                    {...checklistDragProps({ item, isArchived, dragHandlers, dragState })}
                  />
                )
              })}
            </Checklist.Table>
          </div>
        ))
        .exhaustive()}
    </div>
  )
}

type STFolderChecklistItemProps = {
  send: (message: ChecklistMessage) => void
  isArchived: boolean
  item: STChecklistItem
  incompleteReason: STMissingReason | undefined
  documents: STDocument[]
  onSelectDocument: (documentId: string) => void
} & ReturnType<typeof checklistDragProps>

function STFolderChecklistItem({
  send,
  isArchived,
  documents,
  item,
  incompleteReason,
  onSelectDocument,
  ...dragProps
}: STFolderChecklistItemProps) {
  return (
    <ChecklistItemRow
      item={item}
      documents={documents}
      incompleteReason={incompleteReason}
      onSelectDocument={onSelectDocument}
      {...dragProps}
      checkbox={
        <Checklist.CheckboxCell
          disabled={isArchived}
          checked={item.status == 'complete'}
          onChange={() =>
            send({ type: 'openChecklistDialog', request: { mode: 'manageCompletion', item } })
          }
        />
      }
      actions={
        <>
          <ChecklistVisibilityButton item={item} disabled={isArchived} send={send} />

          <ChecklistItemActions
            name={item.name}
            disabled={isArchived}
            onAction={(mode) => send({ type: 'openChecklistDialog', request: { mode, item } })}
          />
        </>
      }
    />
  )
}
